import cn from "classnames";
import { getLastPoint } from "helpers/bidpoint";
import { plainDate } from "helpers/date";
import { accessPermitRelationTypesEnum, bidPointTypesEnum, bidStatusesEnum } from "helpers/enums";
import { GEOZONE_FIELDS_BY_REGION_TYPES } from "helpers/map";
import uniq from "lodash/uniq";
import moment from "moment";
import { ITEM_ROOT } from "odata-query";
import { createRoot } from "react-dom/client";
import { pointFactEndDate, pointFactStartDate } from "./dateValues";
import templates from "./templates";
import { carPlanningSubgroups } from "./valueModes";

export function initialValue(data) {
    const itemIds = new Set();
    const addUniqueItem = (acc, item) => {
        if (itemIds.has(item.id)) {
            return false;
        }
        itemIds.add(item.id);
        acc.items.push(item);
        return true;
    };

    return (data?.data ?? []).reduce(
        (acc, { bids, driverShift, serviceWorks, ...item }) => {
            const groupItemIds = [];
            let hasServiceWorkLine = false;
            const activeBidLastPoint = () => {
                if (!item.bidId) {
                    return null;
                }
                const bid = bids.find((b) => b.bidId === item.bidId);
                if (!bid) {
                    return null;
                }
                return getLastPoint(bid.bidPoints);
            };
            const lastPoint = activeBidLastPoint() ?? {};
            (bids || []).forEach(({ bidPoints, itemChangePoints, ...bid }) => {
                if (!acc.bidItemIds[bid.bidId]) {
                    acc.bidItemIds[bid.bidId] = [];
                }
                if (!acc.bidItemSubgroups[bid.bidId]) {
                    acc.bidItemSubgroups[bid.bidId] = {};
                }
                const points = [...bidPoints, ...itemChangePoints].sort(
                    (a, b) => new Date(a.planEnterDateOffset).getTime() - new Date(b.planEnterDateOffset).getTime(),
                );
                const loadPointIndex = points.findIndex((item) =>
                    [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type),
                );
                const newPoints = () => {
                    const items = [{ ...points[0], isEmptyMileage: !!loadPointIndex }, points[points.length - 1]];
                    if (loadPointIndex > 0) {
                        items.splice(1, 0, points[loadPointIndex]);
                    }
                    return uniq(items, "id");
                };
                const filteredPoints = newPoints();
                filteredPoints.forEach((point, i) => {
                    const nextPoint = filteredPoints[i + 1];
                    if (nextPoint) {
                        const isLate = nextPoint.manualEstimatedLeaveDate
                            ? moment(nextPoint.manualEstimatedLeaveDate) > moment(nextPoint.planLeaveDateOffset)
                            : nextPoint.estimatedDate
                              ? moment(nextPoint.estimatedDate) > moment(nextPoint.planEnterDateOffset)
                              : false;
                        const pointClassnames = {
                            "car-planning__icon": [point.type, nextPoint.type].some((type) =>
                                [bidPointTypesEnum.ServicePoint, undefined].includes(type),
                            ),
                            "car-planning__icon--start-service-point": point.type === bidPointTypesEnum.ServicePoint,
                            "car-planning__icon--end-service-point": nextPoint.type === bidPointTypesEnum.ServicePoint,
                            "car-planning__icon--start-item-change": point.type === undefined,
                            "car-planning__icon--end-item-change": nextPoint.type === undefined,
                            "car-planning__empty-mileage": point.isEmptyMileage || bid.isEmptyMileageBid,
                        };
                        const planSubgroup = () => {
                            if (point.isEmptyMileage) {
                                return carPlanningSubgroups.EmptyMileagePlan;
                            }
                            return bid.status === bidStatusesEnum.New
                                ? carPlanningSubgroups.Draft
                                : carPlanningSubgroups.Plan;
                        };
                        const lastPointData =
                            lastPoint.id === nextPoint.id
                                ? {
                                      factEndDate: lastPoint.autoLeftAt,
                                      lastPointLoadUnloadedAtByLogist: lastPoint.loadUnloadedAtByLogist,
                                      manualEstimatedLeaveDate: lastPoint.manualEstimatedLeaveDate,
                                      lastPointPlanEnterDateOffset: lastPoint.planEnterDateOffset,
                                      activeBidLastPointAddress: `${lastPoint.geozone?.state}, ${lastPoint.geozone?.city}`,
                                      bidStatus: bid.status,
                                  }
                                : {};
                        const addRangeItem = (rangeItem) => {
                            if (addUniqueItem(acc, rangeItem)) {
                                acc.bidItemIds[bid.bidId].push(rangeItem.id);
                                acc.bidItemSubgroups[bid.bidId][rangeItem.id] = rangeItem.subgroup;
                                groupItemIds.push(rangeItem.id);
                            }
                        };
                        const planItem = (id, subgroup = planSubgroup()) => ({
                            startPoint: point,
                            endPoint: nextPoint,
                            id,
                            subgroup,
                            subgroupOrder: bid.status === bidStatusesEnum.New ? 3 : 1,
                            group: `car_${item.car.id}`,
                            start: plainDate(point.planEnterDateOffset),
                            end: plainDate(nextPoint.planLeaveDateOffset || nextPoint.planEnterDateOffset),
                            pointType: "plan",
                            type: "range",
                            template: "range",
                            className: cn({
                                plan: bid.status !== bidStatusesEnum.New,
                                draft: bid.status === bidStatusesEnum.New,
                                "border-danger": isLate,
                                ...pointClassnames,
                            }),
                            bidData: bid,
                            car: item.car,
                            estimationComment: item.estimationComment,
                            ...lastPointData,
                        });
                        const factItem = (id) => ({
                            startPoint: point,
                            endPoint: nextPoint,
                            id,
                            subgroup: point.isEmptyMileage
                                ? carPlanningSubgroups.EmptyMileageFact
                                : carPlanningSubgroups.Fact,
                            subgroupOrder: 2,
                            group: `car_${item.car.id}`,
                            start: plainDate(pointFactStartDate(point)),
                            end: plainDate(pointFactEndDate(nextPoint)),
                            unloadAt: nextPoint.loadUnloadedAtByLogist || nextPoint.autoLeftAt,
                            pointType: "fact",
                            type: "range",
                            template: "range",
                            className: cn("fact", {
                                "border-danger": isLate,
                                "car-planning__empty-mileage": point.isEmptyMileage || bid.isEmptyMileageBid,
                            }),
                            bidData: bid,
                            car: item.car,
                            estimationComment: item.estimationComment,
                            ...lastPointData,
                        });
                        const hasFactDates = Boolean(pointFactStartDate(point) && pointFactEndDate(nextPoint));

                        addRangeItem(planItem(`${bid.bidId}_${point.id}_plan`));
                        if (hasFactDates) {
                            addRangeItem(factItem(`${bid.bidId}_${point.id}_fact`));
                        } else {
                            addRangeItem(
                                planItem(
                                    `${bid.bidId}_${point.id}_fact-fallback`,
                                    point.isEmptyMileage
                                        ? carPlanningSubgroups.EmptyMileageFactFallbackPlan
                                        : carPlanningSubgroups.FactFallbackPlan,
                                ),
                            );
                        }
                    }
                });
            });
            const driverShiftStart = driverShift && plainDate(driverShift.plannedStartDateOfNextShift);
            if (driverShiftStart) {
                addUniqueItem(acc, {
                    id: `${item.car.id}_driver-shift`,
                    subgroup: carPlanningSubgroups.Marker,
                    subgroupOrder: 1,
                    group: `car_${item.car.id}`,
                    start: driverShiftStart,
                    end: driverShiftStart,
                    type: "range",
                    template: "driverShift",
                    className: "car-planning__range-point",
                    shiftData: driverShift,
                    selectable: false,
                });
            }
            if (serviceWorks) {
                serviceWorks.forEach((serviceWork, i) => {
                    const start = plainDate(serviceWork.startDate);
                    const end = plainDate(serviceWork.endDate);

                    if (!start || !end || moment(end).isSameOrBefore(start)) {
                        return;
                    }

                    const serviceWorkAdded = addUniqueItem(acc, {
                        id: `${item.car.id}_service-work_${serviceWork.id ?? serviceWork.serviceWorkId ?? i}`,
                        subgroup: carPlanningSubgroups.ServiceWork,
                        subgroupOrder: 4,
                        group: `car_${item.car.id}`,
                        start,
                        end,
                        type: "range",
                        template: "serviceWork",
                        className: "car-planning__service-work",
                        serviceWorkData: serviceWork,
                        car: item.car,
                        selectable: false,
                    });
                    hasServiceWorkLine = hasServiceWorkLine || serviceWorkAdded;
                });
            }
            acc.groups.push({
                ...item,
                lastPoint,
                hasBid: bids?.length > 0,
                factEndDate: lastPoint.autoLeftAt,
                lastPointLoadUnloadedAtByLogist: lastPoint.loadUnloadedAtByLogist,
                lastPointPlanEnterDateOffset: lastPoint.planEnterDateOffset,
                activeBidLastPointAddress: `${lastPoint.geozone?.state}, ${lastPoint.geozone?.city}`,
                driverShift,
                serviceWorks,
                carNumbersOnly: item.car.number.replace(/\D/g, ""),
                carLettersOnly: item.car.number.replace(/[^а-яА-Я]/g, ""),
                id: `car_${item.car.id}`,
                className: cn(item.className, { "car-planning__group--with-service-work": hasServiceWorkLine }),
                groupItemIds,
                template: "group",
                subgroupOrder: (a, b) => a.subgroupOrder - b.subgroupOrder,
            });

            return acc;
        },
        {
            items: [],
            groups: [],
            bidItemIds: {},
            bidItemSubgroups: {},
            counters: {
                hasPlannedBidCount: data?.hasPlannedBidCount ?? 0,
                hasNoPlannedBidCount: data?.hasNoPlannedBidCount ?? 0,
                noActiveBidCount: data?.noActiveBidCount ?? 0,
                atUnloadingCount: data?.atUnloadingCount ?? 0,
                bookedCount: data?.bookedCount ?? 0,
            },
        },
    );
}

export function calculateItemClassName(element) {
    if (element.scrollWidth > element.offsetWidth) {
        const cname = element.scrollWidth < 200 ? "car-planning__item--short-200" : "car-planning__item--short-300";
        element.parentElement.classList.add("car-planning__item--short", cname);
    } else {
        element.parentElement.classList.remove(
            "car-planning__item--short",
            "car-planning__item--short-200",
            "car-planning__item--short-300",
        );
    }
}

export function templateRenderer(item, element, options) {
    if (!item || !element) {
        return;
    }

    if (!element.hasAttribute("data-is-rendered")) {
        element.setAttribute("data-is-rendered", "true");
        const root = createRoot(element);
        root.render(templates[item.template || item.type](item, options));
        element.timelineItemRoot = root;
    } else {
        element.timelineItemRoot.render(templates[item.template](item, options));
    }
    if (item.template === "range") {
        setTimeout(() => calculateItemClassName(element), 100);
    }
    return "";
}

export function buildFilter(filtered, profile) {
    const filter = {};
    for (const key in filtered) {
        const item = filtered[key];
        const { value, name, option } = item;
        switch (name) {
            case "accessPermitIds": {
                const { carPermitIds, trailerPermitIds, driverPermitIds } = option.reduce(
                    (acc, item) => {
                        switch (item.relationType) {
                            case accessPermitRelationTypesEnum.Car:
                                acc.carPermitIds.push(item.value);
                                break;
                            case accessPermitRelationTypesEnum.Trailer:
                                acc.trailerPermitIds.push(item.value);
                                break;
                            case accessPermitRelationTypesEnum.Driver:
                                acc.driverPermitIds.push(item.value);
                                break;
                            default:
                                break;
                        }
                        return acc;
                    },
                    {
                        carPermitIds: [],
                        trailerPermitIds: [],
                        driverPermitIds: [],
                    },
                );
                if (carPermitIds.length > 0) {
                    filter.car = { accessPermitIds: { any: { [ITEM_ROOT]: { in: value } } } };
                }
                if (trailerPermitIds.length > 0) {
                    filter.trailer = { accessPermitIds: { any: { [ITEM_ROOT]: { in: value } } } };
                }
                if (driverPermitIds.length > 0) {
                    filter.driver = { accessPermitIds: { any: { [ITEM_ROOT]: { in: value } } } };
                }
                break;
            }
            case "carGroupId":
                filter.carGroups = { any: { id: { in: value } } };
                break;
            case "location":
                filter[`bid/lastBidPoint/geozone/${GEOZONE_FIELDS_BY_REGION_TYPES[value.type]}`] = { in: [value.id] };
                break;
            case "regionGroupId":
                filter["bid/lastBidPoint/geozone/stateId"] = { in: value.regionIds };
                break;
            case "carId":
                filter["car/id"] = { in: value };
                break;
            case "onlyLogistCars":
                filter["logist/id"] = profile?.id;
                break;
            case "hideInactiveCars":
                filter["car/isActive"] = true;
                break;
            case "onlyWithDriverNeedsShiftChange":
                filter.or = [{ "driver/needsShiftChangeComment": { ne: null } }, { driverShift: { ne: null } }];
                break;
            case "onlyWithExternalBids":
                filter.carExternalOutput = { ne: null };
                break;
            case "onlyWithServiceWork":
                filter.serviceWorks = { any: { [ITEM_ROOT]: { ne: null } } };
                break;
            case "transportColumn":
                filter["car/transportColumn/id"] = Array.isArray(value) ? { in: value } : value;
                break;
            case "carPlanningStatus":
                filter.carPlanningStatus = { in: value };
                break;
            default:
                filter[name] = { in: value };
                break;
        }
    }
    return filter;
}
